import { fromUnixTime } from 'date-fns';
import { Draft, produce } from 'immer';
import {
  DIFF_BAND_TRUNCATION_SERIES_NAME,
  LOWER_BAND_TRUNCATION_SERIES_NAME,
  Notice,
  PrometheusQuerySpec,
  TimeSeriesData,
  TimeSeriesDataResponse,
  UPPER_BAND_TRUNCATION_SERIES_NAME,
} from '@/core';
import { ParsedValueTuple, parseValueTuple } from '@/prometheus-plugin/model';
import { ResponseHandler, ResponseHandlerFn, ResultType } from '../types';
import { NATIVE_HISTOGRAM_ERROR_MESSAGE, transformPromRangeQueryToTimeSeries } from './utils';

export const timeSeriesHandler: ResponseHandlerFn<TimeSeriesDataResponse> = (result, metadata, notices, context) => {
  const spec = context.spec as PrometheusQuerySpec;

  const series = transformPromRangeQueryToTimeSeries({
    result,
    alias: context.alias ?? '',
    query: context.query,
    queryWithGlobalVariables: spec.query, // Use the original query for global variables
    seriesName: context.series_name,
    seriesNameFormat: context.series_name_format,
  });

  let chartData: TimeSeriesData = {
    // Return the time range and step we actually used for the query
    timeRange: { start: fromUnixTime(context.start), end: fromUnixTime(context.end) },
    stepMs: context.step * 1000,
    series,
    metadata,
  };

  // If data was truncated, and the truncation strategy was set in the query, add the truncated series
  if (context.response?.status === 'success' && context.response.truncationDetails) {
    const hasTruncationStrategySet = context.hasTruncationStrategySet;
    const isStacked = context.isStacked;

    if (hasTruncationStrategySet) {
      metadata.truncated = true;

      // If its a stack chart, we only need to add the total series
      if (isStacked) {
        const totalSeries = context.response.truncationDetails.truncatedTotals.map((v) => {
          return parseValueTuple(v);
        });
        const name = `${context.alias ? `${context.alias} ` : ''}{__truncated__="true"}`;
        chartData = produce(chartData, (draft) => {
          draft.series.unshift({
            config: {
              color: 'gray', // (TODO: replace with better color palette for this)
            },
            name: name,
            formattedName: name,
            labels: {
              __truncation_band__: 'stack',
            },
            values: totalSeries,
          });
        });
      } else {
        const upperBandSeries = context.response.truncationDetails.upperBounds.map((v) => {
          return parseValueTuple(v);
        });
        const lowerBandSeries = context.response.truncationDetails.lowerBounds.map((v) => {
          return parseValueTuple(v);
        });

        // For the confidence band effect, the upper band should contain the difference
        // between upper and lower bounds, not the absolute upper values
        const diffBandSeries: ParsedValueTuple[] = context.response.truncationDetails.upperBounds.map((v, index) => {
          const upperValue = parseValueTuple(v);
          const lowerValue = lowerBandSeries[index];

          // Return the difference (upper - lower) for stacking
          if (lowerValue && typeof upperValue[1] === 'number' && typeof lowerValue[1] === 'number') {
            const confidenceBandDifference = upperValue[1] - lowerValue[1];
            return [upperValue[0], confidenceBandDifference];
          }
          return upperValue;
        });

        chartData = produce(chartData, (draft: Draft<TimeSeriesData>) => {
          draft.series.unshift({
            config: {
              color: 'gray', // (TODO: replace with better color palette for this)
            },
            name: DIFF_BAND_TRUNCATION_SERIES_NAME,
            formattedName: DIFF_BAND_TRUNCATION_SERIES_NAME,
            labels: {
              __truncation_band__: 'diff',
            },
            values: diffBandSeries,
          });
          draft.series.unshift({
            config: {
              color: 'gray', // (TODO: replace with better color palette for this)
            },
            name: UPPER_BAND_TRUNCATION_SERIES_NAME,
            formattedName: UPPER_BAND_TRUNCATION_SERIES_NAME,
            labels: {
              __truncation_band__: 'upper',
            },
            values: upperBandSeries,
          });
          draft.series.unshift({
            config: {
              color: 'gray', // (TODO: replace with better color palette for this)
            },
            name: LOWER_BAND_TRUNCATION_SERIES_NAME,
            formattedName: LOWER_BAND_TRUNCATION_SERIES_NAME,
            labels: {
              __truncation_band__: 'lower',
            },
            values: lowerBandSeries,
          });
        });
      }
    }
  }

  return {
    type: 'TimeSeries',
    data: chartData,
  };
};

export const timeSeriesResponseHandler: ResponseHandler = {
  [ResultType.NativeHistogram]: () => {
    throw new Error(NATIVE_HISTOGRAM_ERROR_MESSAGE);
  },
  [ResultType.TimeSeriesHistogram]: (result, metadata, notices, context) => {
    const HISTOGRAM_WRONG_VIZ_NOTICE: Notice = {
      type: 'warning',
      message: 'Queries resulted in histograms.',
      description: 'Consider calculating aggregated quantile by adding histogram_quantile() or using a heatmap.',
    };

    notices.push(HISTOGRAM_WRONG_VIZ_NOTICE);

    const series = transformPromRangeQueryToTimeSeries({
      result,
      alias: context.alias ?? '',
      query: context.query,
      seriesName: context.series_name,
      seriesNameFormat: context.series_name_format,
    });

    const timeSeriesData: TimeSeriesData = {
      timeRange: { start: fromUnixTime(context.start), end: fromUnixTime(context.end) },
      stepMs: context.step * 1000,
      series,
      metadata: {
        ...metadata,
        notices,
      },
    };

    return {
      type: 'TimeSeries',
      data: timeSeriesData,
    };
  },
  [ResultType.TimeSeries]: timeSeriesHandler,
};
